import { useApolloClient } from '@apollo/client';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Box, Button, Divider, Stack, Typography } from '@mui/material';
import {
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GridRenderCellParams,
} from '@mui/x-data-grid-pro';
import { assertNotNull, validLanguages } from '@remote-voice/utilities';
import csvtojson from 'csvtojson';
import isMobile from 'is-mobile';
import fileDownload from 'js-file-download';
import PopupState, { bindTrigger, bindMenu } from 'material-ui-popup-state';
import Queue from 'promise-queue';
import { useState, useEffect } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import AddButton from '@/components/atoms/AddButton';
import { AdminContent } from '@/components/atoms/AdminPageParts';
import DeleteButton from '@/components/atoms/DeleteButton';
import DraggableDataGrid from '@/components/atoms/DraggableDataGrid';
import { DragHandle } from '@/components/atoms/DragHandle';
import NameEditableCell from '@/components/atoms/NameEditableCell';
import { useCheckPermission } from '@/components/hooks/useCheckPermission';
import { useSetHeaderTitle } from '@/components/hooks/useHeaderTitle';
import { useSnackbar } from '@/components/hooks/useSnackbar';
import { useUserPermission } from '@/components/hooks/useUserPermission';
import { CSVOperationMenu } from '@/components/molecules/admin/CSVOperationMenu';
import { DeletionFooterMenu } from '@/components/molecules/admin/DeletionFooterMenu';
import { ResponsiveFormHeader } from '@/components/molecules/admin/ResponsiveFormHeader';
import { useCategoryDialog } from '@/components/organisms/categoryDialog/useCategoryDialog';
import useConfirmDeleteDialog from '@/components/organisms/confirmDialog/useConfirmDeleteDialog';
import useConfirmOkDialog from '@/components/organisms/confirmDialog/useConfirmOkDialog';
import useDropzoneDialog from '@/components/organisms/dropzoneDialog/useDropzoneDialog';
import ImportErrorDialog from '@/components/organisms/ImportErrorDialog';
import useTemplateSelectorDialog from '@/components/organisms/templateSelectorDialog/useTemplateSelectorDialog';
import TemplateUploadProgressDialog from '@/components/organisms/templateUploadProgressDialog/TemplateUploadProgressDialog';

import {
  ImportTemplateMessage,
  TemplateMessageCategory,
  useAddTemplateMessageCategoryMutation,
  useChatRoomsQuery,
  useEditChatRoomMutation,
  useEditTemplateMessageCategoryMutation,
  useImportTemplateMessagesMutation,
  useRemoveTemplateMessageCategoriesMutation,
  useTemplateMessageCategoriesQuery,
  useTemplateMessagesLazyQuery,
} from '@/types/graphql';
import templateMessageCsvStringify from '@/utils/templateMessageCsvStringify';

const promiseQueue = new Queue(5);

const CategoryIndex = () => {
  useCheckPermission({ tenantCompanyAdmin: true });

  const { t } = useTranslation('admin');
  const { t: tCommon } = useTranslation('common');
  useSetHeaderTitle(tCommon('breadcrumb.templateAdmin'));

  const categoriesResult = useTemplateMessageCategoriesQuery({
    variables: { input: {} },
  });

  const [rows, setRows] = useState<TemplateMessageCategory[]>([]);
  useEffect(() => {
    setRows(categoriesResult.data?.templateMessageCategories ?? []);
  }, [categoriesResult.data?.templateMessageCategories]);
  const [removeCategories, removeCategoriesResult] =
    useRemoveTemplateMessageCategoriesMutation();
  const [getTemplateMessages, getTemplateMessagesResult] =
    useTemplateMessagesLazyQuery();
  const [importTemplateMessages, importTemplateMessagesResult] =
    useImportTemplateMessagesMutation();
  const [editTemplateMessageCategory, editTemplateMessageCategoryResult] =
    useEditTemplateMessageCategoryMutation();
  const client = useApolloClient();

  // インポート結果表示用
  const [importedTemplateMessages, setImportedTemplateMessages] = useState<
    ImportTemplateMessage[] | undefined
  >();
  const chatRoomsResult = useChatRoomsQuery({ variables: { input: {} } });
  const templateSelectorDialog = useTemplateSelectorDialog();
  const [editChatRoom] = useEditChatRoomMutation();

  const permission = useUserPermission();
  const [addTemplateMessageCategory] = useAddTemplateMessageCategoryMutation();

  const confirmOkDialog = useConfirmOkDialog();
  const navigate = useNavigate();
  const showSnackbar = useSnackbar();

  const [isInDeleteState, setIsInDeleteState] = useState(false);
  const [deletee, setDeletee] = useState<string[]>([]);
  const confirmDelete = useConfirmDeleteDialog();
  const dropzoneDialog = useDropzoneDialog();
  const categoryDialog = useCategoryDialog();

  const saveRows = async (newRows: TemplateMessageCategory[]) => {
    for (const x of newRows) {
      await promiseQueue.add(() =>
        editTemplateMessageCategory({
          variables: {
            input: {
              id: x.id,
              name: x.name,
              absoluteOrder: x.absoluteOrder,
            },
          },
        })
      );
    }
  };

  return (
    <Box
      sx={{
        display: 'contents',
        flexDirection: 'column',
        height: chatRoomsResult.data?.chatRooms ? 'calc(100vh - 80px)' : '100%',
        maxHeight: chatRoomsResult.data?.chatRooms
          ? 'calc(100vh - 80px)'
          : 'none',
      }}
    >
      <AdminContent>
        <ResponsiveFormHeader
          title={t('template.list')}
          titleVarient="h4"
          toolbuttons={[
            <AddButton
              key="addCategory"
              onClick={async () => {
                const result = await categoryDialog.open({
                  title: t('template.createNewCategory'),
                });

                if (result != null) {
                  const added = await addTemplateMessageCategory({
                    variables: {
                      input: {
                        name: result,
                        tenantCompanyId: permission.tenantCompanyId ?? '',
                      },
                    },
                  });
                  if (added.errors) throw added.errors;
                  assertNotNull(added.data);
                  await categoriesResult.refetch();
                }
              }}
            >
              {t('template.newCategory')}
            </AddButton>,
            <AddButton key="add" onClick={() => navigate('add')}>
              {t('template.newTemplate')}
            </AddButton>,
            // PC端末の時のみCSV操作ボタンを表示
            ...(isMobile() === false
              ? [
                  <PopupState key="csv" variant="popover">
                    {(popupState) => (
                      <React.Fragment>
                        <Button
                          sx={{ minWidth: 50, maxWidth: 200 }}
                          variant="contained"
                          {...bindTrigger(popupState)}
                        >
                          {t('template.csvOperation')}
                        </Button>
                        <CSVOperationMenu
                          {...bindMenu(popupState)}
                          // ひな形ダウンロード
                          onClickDownload={() => {
                            popupState.close();
                            // 空のCSVをダウンロード
                            fileDownload(
                              templateMessageCsvStringify([]),
                              'rg-template.csv'
                            );
                          }}
                          onClickExport={async () => {
                            popupState.close();
                            const result = await getTemplateMessages({
                              variables: {
                                input: {},
                              },
                            });
                            if (result.error) {
                              throw result.error;
                            }
                            assertNotNull(result.data);

                            // カテゴリのabsoluteOrderでソート、同じカテゴリ内ではtemplateMessage.orderでソート
                            const sortedTemplateMessages = [
                              ...result.data.templateMessages,
                            ].sort((a, b) => {
                              // まずカテゴリのabsoluteOrderでソート
                              const categoryOrder =
                                (a.category.absoluteOrder ?? 0) -
                                (b.category.absoluteOrder ?? 0);
                              if (categoryOrder !== 0) {
                                return categoryOrder;
                              }
                              // カテゴリが同じ場合はtemplateMessage.orderでソート
                              return (a.order ?? 0) - (b.order ?? 0);
                            });

                            const text = templateMessageCsvStringify(
                              sortedTemplateMessages.map((x) => {
                                const langToMsg = (lang: string) =>
                                  x.languages.find((y) => y.language === lang)
                                    ?.message ?? '';
                                return {
                                  ID: x.id,
                                  categoryId: x.category.id,
                                  categoryName: x.category.name,
                                  ja: langToMsg('ja'),
                                  en: langToMsg('en'),
                                  ko: langToMsg('ko'),
                                  zh: langToMsg('zh'),
                                  'zh-TW': langToMsg('zh-TW'),
                                  es: langToMsg('es'),
                                  fil: langToMsg('fil'),
                                  fr: langToMsg('fr'),
                                  id: langToMsg('id'),
                                  my: langToMsg('my'),
                                  'pt-BR': langToMsg('pt-BR'),
                                  th: langToMsg('th'),
                                  vi: langToMsg('vi'),
                                  ne: langToMsg('ne'),
                                };
                              })
                            );
                            fileDownload(text, 'rg-export.csv');
                          }}
                          onClickImport={async () => {
                            popupState.close();
                            const fileList = await dropzoneDialog.open({});
                            const file = fileList?.[0];

                            if (file) {
                              const text = await file.text();
                              const records: {
                                [index: string]: string | undefined;
                              }[] = await csvtojson().fromString(text);

                              // カラムチェック
                              if (
                                records.some((record) => {
                                  const keys = Object.keys(record);
                                  const columns = [
                                    'ID',
                                    'categoryId',
                                    'categoryName',
                                    ...validLanguages,
                                  ];
                                  return (
                                    // 余計なカラムがあるか
                                    keys.filter(
                                      (x) =>
                                        columns.find((y) => x === y) == null
                                    ).length >= 1 ||
                                    // カラムが足りないか
                                    columns.filter(
                                      (x) => keys.find((y) => x === y) == null
                                    ).length >= 1
                                  );
                                })
                              ) {
                                showSnackbar(
                                  'error',
                                  t('template.csvImportColumnError')
                                );
                                return;
                              }

                              if (records.length > 1000) {
                                showSnackbar(
                                  'error',
                                  t('template.importError.invalidCSVFormat')
                                );
                                return;
                              }

                              let replacedBreak = false;
                              const result = await (async () => {
                                try {
                                  const result = await importTemplateMessages({
                                    variables: {
                                      input: {
                                        templateMessages: records.map(
                                          (record) => ({
                                            templateMessageId: record.ID ?? '',
                                            templateMessageCategoryId:
                                              record.categoryId ?? '',
                                            templateMessageCategoryName:
                                              record.categoryName ?? '',
                                            languages: validLanguages.map(
                                              (x) => {
                                                const message = record[x] ?? '';
                                                const replacedMsg =
                                                  message.replace(/\n/g, ' ');
                                                if (message !== replacedMsg)
                                                  replacedBreak = true;
                                                return {
                                                  language: x,
                                                  message: replacedMsg,
                                                };
                                              }
                                            ),
                                          })
                                        ),
                                        fileName: file.name,
                                      },
                                    },
                                  });
                                  if (result.errors?.[0] != null) {
                                    throw result.errors[0];
                                  }
                                  return result;
                                } catch (err: any) {
                                  console.error(err);
                                  showSnackbar(
                                    'error',
                                    tCommon('message.unexpectedError')
                                  );
                                }
                              })();
                              if (result == null) {
                                client.refetchQueries({
                                  include: [
                                    'templateMessageTranslationProgress',
                                  ],
                                });
                                return;
                              }
                              assertNotNull(result.data);

                              if (
                                result.data.importTemplateMessages.find(
                                  (x) => x.errorCode !== 0
                                )
                              ) {
                                // 結果を表示する
                                setImportedTemplateMessages(
                                  result.data.importTemplateMessages
                                );
                              } else {
                                // 成功したので画面更新
                                categoriesResult.refetch();
                                await confirmOkDialog.open({
                                  message:
                                    t('template.csvImportedMsg') +
                                    (replacedBreak
                                      ? t('template.replacedBreak')
                                      : ''),
                                  title: '',
                                });
                                client.refetchQueries({
                                  include: [
                                    'templateMessageTranslationProgress',
                                  ],
                                });
                              }
                            }
                          }}
                        />
                      </React.Fragment>
                    )}
                  </PopupState>,
                ]
              : []),
            // 削除モードの時は削除ボタンを非表示
            ...(isInDeleteState === false
              ? [
                  <DeleteButton
                    key="remove"
                    onClick={() => setIsInDeleteState(true)}
                  />,
                ]
              : []),
          ]}
        />
        <Divider sx={{ mt: 4 }} />
        <DraggableDataGrid
          autoHeight
          columnHeaderHeight={40}
          columns={[
            {
              field: 'name',
              headerName: t('template.category'),
              minWidth: 150,
              flex: 1,
              sortable: false,
              renderCell: (params) => (
                <NameEditableCell
                  name={params.value}
                  onClickEdit={async () => {
                    const result = await categoryDialog.open({
                      title: t('template.categoryName'),
                      name: params.value,
                    });

                    if (result != null && result !== params.value) {
                      await editTemplateMessageCategory({
                        variables: {
                          input: {
                            id: params.id.toString(),
                            name: result,
                          },
                        },
                      });
                      await categoriesResult.refetch();
                    }
                  }}
                  enableLongPress={true}
                />
              ),
            },
            {
              field: 'templateMessagesCount',
              headerName: isInDeleteState ? '' : t('template.count'),
              width: 60,
              minWidth: 60,
              sortable: false,
              valueGetter: (params) => params.value + t('template.countUnit'),
            },
            ...(isInDeleteState
              ? [
                  {
                    ...GRID_CHECKBOX_SELECTION_COL_DEF,
                    renderHeader: () => tCommon('command.delete'),
                  },
                ]
              : [
                  {
                    field: ' ',
                    type: 'actions',
                    width: 50,
                    minWidth: 50,
                    renderCell: (params: GridRenderCellParams) => [
                      <KeyboardArrowRightIcon
                        key="move"
                        sx={{ color: (t) => t.palette.grey[500] }}
                      />,
                      <DragHandle
                        rowId={params.id.toString()}
                        key={params.id}
                      />,
                    ],
                  },
                ]),
          ]}
          sx={{
            '& .MuiDataGrid-virtualScrollerContent': {
              backgroundColor: 'transparent',
            },
            '& .MuiDataGrid-cell[data-field="templateMessagesCount"]': {
              justifyContent: 'flex-end',
            },
          }}
          rows={[...rows]
            .sort((a, b) => (a.absoluteOrder ?? 0) - (b.absoluteOrder ?? 0))
            .map((category) => ({
              id: category.id,
              name: category.name,
              templateMessagesCount: category.templateMessagesCount,
              absoluteOrder: category.absoluteOrder,
            }))}
          rowHeight={60}
          hideFooter
          disableColumnMenu
          disableMultipleColumnsSorting
          onRowClick={(params) => {
            if (isInDeleteState === false) {
              navigate(`${params.id}/`);
            }
          }}
          onRowSelectionModelChange={(selection) =>
            setDeletee(selection.map((x) => x.toString()))
          }
          checkboxSelection={isInDeleteState}
          loading={
            removeCategoriesResult.loading ||
            getTemplateMessagesResult.loading ||
            importTemplateMessagesResult.loading ||
            categoriesResult.loading ||
            editTemplateMessageCategoryResult.loading
          }
          onChange={async (params: any[], _oldIndex, _newIndex) => {
            // 並べ替え
            // paramsは既に正しい順序になっているので、idでrowsを検索してabsoluteOrderを振りなおすだけ
            const newRows = params.map((p, i) => {
              const row = rows.find((r) => r.id === p.id);
              if (!row) throw new Error('Row not found');
              return { ...row, absoluteOrder: i + 1 };
            });

            setRows(newRows);
            await saveRows(newRows);
            await categoriesResult.refetch();
          }}
        />
      </AdminContent>

      {!!chatRoomsResult.data?.chatRooms && (
        <Box
          sx={{
            minHeight: '140px',
            maxHeight: '400px',
            pb: isInDeleteState ? '80px' : 0,
          }}
        >
          <Stack
            direction="row"
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.6)',
              backdropFilter: 'blur(8px)',
              width: 1,
              pt: 2,
              position: 'fixed',
              bottom: isInDeleteState ? 80 : 0,
              left: 0,
              zIndex: 10,
            }}
            justifyContent="center"
            alignItems="center"
          >
            <Stack
              direction="column"
              sx={{
                maxWidth: 1100,
                width: '100%',
                margin: '0 auto',
                px: '20px',
                pt: 3,
              }}
            >
              <Typography variant="h4">
                {t('template.setTemplateToRoom')}
              </Typography>
              <Stack
                direction={'row'}
                my={1}
                flexWrap={'wrap'}
                sx={{ maxHeight: '150px', overflow: 'auto' }}
              >
                {chatRoomsResult.data?.chatRooms.map((r) => (
                  <Button
                    key={r.id}
                    sx={{ mt: 1, mr: 1 }}
                    variant="outlined"
                    onClick={async () => {
                      const selectedCategories =
                        await templateSelectorDialog.open({
                          templateCategories:
                            categoriesResult.data?.templateMessageCategories ??
                            [],
                          defaultSelection: r.templateCategoryIds,
                        });
                      if (selectedCategories != null) {
                        await editChatRoom({
                          variables: {
                            input: {
                              templateCategoryIds: selectedCategories,
                              audioConfirmation: r.audioConfirmation,
                              capacity: r.capacity,
                              dualLangTranslation: r.dualLangTranslation,
                              enableAutoReset: r.enableAutoReset,
                              homophoneCategoryIds: r.homophoneCategoryIds,
                              id: r.id,
                              isGuestInputRestricted: r.isGuestInputRestricted,
                              isGuestMicControlLinked:
                                r.isGuestMicControlLinked,
                              isGuideMicControlLinked:
                                r.isGuideMicControlLinked,
                              isNotifyOnMessageEnabled:
                                r.isNotifyOnMessageEnabled,
                              isWaitingRoomEnabled: r.isWaitingRoomEnabled,
                              isWaitingRoomNotificationEnabled:
                                r.isWaitingRoomNotificationEnabled,
                              name: r.name,
                              translateMethod: r.translateMethod,
                              vadPresetId: r.vadPresetId,
                              isLogSavingDisabled:
                                r.isLogSavingDisabled ?? false,
                            },
                          },
                        });
                        await chatRoomsResult.refetch();
                        showSnackbar(
                          'success',
                          tCommon('message.successSaveChanges')
                        );
                      }
                    }}
                  >
                    {r.name}
                  </Button>
                ))}
              </Stack>
            </Stack>
          </Stack>
        </Box>
      )}

      {isInDeleteState && (
        <DeletionFooterMenu
          deleteButtonDisabled={deletee.length === 0}
          deleteButtonText={t('template.deleteSelectedCategory')}
          onClickDelete={async () => {
            const result = await confirmDelete.open({
              title: t('template.deleteSelectedCategoryMsg'),
              message: t('template.unbackable'),
              okColor: 'primary',
            });
            if (result) {
              await removeCategories({
                variables: { input: { ids: deletee } },
              });
              setDeletee([]);
              setIsInDeleteState(false);
              categoriesResult.refetch();
              showSnackbar('success', t('template.deletedSelectedCategory'));
            }
          }}
          onClickCancel={() => setIsInDeleteState(false)}
        />
      )}
      <ImportErrorDialog
        open={importedTemplateMessages != null}
        onClose={() => {
          setImportedTemplateMessages(undefined);
          client.refetchQueries({
            include: ['templateMessageTranslationProgress'],
          });
        }}
        data={importedTemplateMessages ?? []}
        msgToError={(message: ImportTemplateMessage) => {
          if (message.errorCode === 0) {
            return t('template.importError.none');
          } else if (message.errorCode === 1) {
            return t('template.importError.categoryIsNull');
          } else if (message.errorCode === 2) {
            return t('template.importError.invalidCSVFormat');
          } else if (message.errorCode === 3) {
            return t('template.importError.idNotFound');
          } else if (message.errorCode === 4) {
            return t('template.importError.categoryIdNotFound');
          } else if (message.errorCode === 5) {
            return t('template.importError.messageNotSpecified', {
              name:
                message.languages.find((x) => x.message.trim() === '')
                  ?.language ?? '',
            });
          } else {
            return t('template.importError.other');
          }
        }}
      />
      <TemplateUploadProgressDialog />
    </Box>
  );
};
export default CategoryIndex;
